import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Shield, Sparkles, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import ShweebumCat from "../components/ShweebumCat";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import AdSlot from "../components/AdSlot";

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#F0EEEB]">
      <SiteHeader />
      
      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <ShweebumCat size="lg" />
            <h1 className="font-literary text-3xl sm:text-4xl text-foreground mt-4" data-testid="about-title">
              About You Inspire Designs LLC
            </h1>
            <p className="text-muted-foreground mt-2">The family behind Shweebum</p>
          </motion.div>

          {/* Main Content */}
          <Card className="bg-card mb-8">
            <CardContent className="p-8">
              <div className="prose prose-stone max-w-none">
                <div className="flex items-center gap-2 mb-6">
                  <Heart className="w-6 h-6 text-red-500" />
                  <h2 className="font-literary text-2xl text-foreground m-0">Our Story</h2>
                </div>
                
                <p className="text-foreground leading-relaxed mb-4">
                  <strong>You Inspire Designs LLC</strong> is a small, family-owned operation proudly based in the 
                  <strong> Southeast United States</strong>. We're not a big tech company or a faceless corporation — 
                  we're a family with a passion for creativity and a heart for helping others bring their stories to life.
                </p>

                <p className="text-foreground leading-relaxed mb-4">
                  As parents to <strong>six wonderful children</strong>, we understand the power of imagination. 
                  Every bedtime story, every "what if" question, every creative adventure has taught us that 
                  storytelling is one of the most precious gifts we can nurture. That's why we created Shweebum — 
                  to help writers of all ages and skill levels unlock their creative potential.
                </p>

                <div className="bg-slate-800/50 border-l-4 border-cyan-500 p-4 my-6">
                  <p className="text-foreground italic m-0">
                    "We believe that everyone has a story worth telling. Shweebum is here to help you tell yours."
                  </p>
                </div>

                <div className="flex items-center gap-2 mb-4 mt-8">
                  <Shield className="w-6 h-6 text-blue-600" />
                  <h2 className="font-literary text-2xl text-foreground m-0">Our Values</h2>
                </div>

                <p className="text-foreground leading-relaxed mb-4">
                  We are proudly <strong>Pro-America</strong> and <strong>Pro-Christian</strong>. Our faith and love 
                  for this great nation guide everything we do. We believe in hard work, family values, 
                  and the American dream of building something meaningful.
                </p>

                <p className="text-foreground leading-relaxed mb-4">
                  We created Shweebum (named after our beloved family cat!) because we believe technology 
                  should serve people, not the other way around. Our AI assistant is designed to be a helpful 
                  companion on your writing journey — encouraging, supportive, and always ready to help you 
                  bring your vision to life.
                </p>

                <div className="flex items-center gap-2 mb-4 mt-8">
                  <Sparkles className="w-6 h-6 text-purple-600" />
                  <h2 className="font-literary text-2xl text-foreground m-0">Why "Shweebum"?</h2>
                </div>

                <p className="text-foreground leading-relaxed">
                  Shweebum is our family's black cat who loves to curl up nearby while we work and create. 
                  She's been our quiet companion through countless late nights of building this platform. 
                  Her calm presence and wise eyes inspired us to create an AI assistant that feels like 
                  a friendly companion rather than a cold tool. Just like the real Shweebum, our digital 
                  Shweebum is here to keep you company while you write your masterpiece.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Contact CTA */}
          <AdSlot slot="about-bottom" format="inline" className="max-w-4xl mx-auto" />
          <div className="text-center">
            <p className="text-muted-foreground mb-4">Have questions or want to say hello?</p>
            <Link to="/contact">
              <Button className="bg-cyan-700 hover:bg-cyan-800">
                <Mail className="w-4 h-4 mr-2" />
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default AboutPage;
